import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UsersThree, Eye, PauseCircle } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { JOIN_KEY } from "@/pages/Join";
import { SecuritySection } from "@/components/account/SecuritySection";

export const hasJoinToken = () => !!localStorage.getItem(JOIN_KEY);

export const JoinTeam = () => {
  const api = useApi();
  const qc = useQueryClient();
  const [error, setError] = useState("");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const r = await api.post("/team/accept", { token: localStorage.getItem(JOIN_KEY) });
        localStorage.removeItem(JOIN_KEY);
        toast.success(`You joined the team as ${r.role === "viewer" ? "a viewer" : "an editor"}`);
        qc.invalidateQueries();
      } catch (e) { setError(errText(e, "Couldn't join the team")); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dismiss = () => { localStorage.removeItem(JOIN_KEY); qc.invalidateQueries({ queryKey: ["me"] }); window.location.replace("/app"); };
  return (
    <div data-testid="join-team" className="mx-auto mt-16 max-w-md rounded-xl border border-[#2A2A2A] bg-[#121212] p-8">
      <UsersThree size={28} className="text-[#00FF9D]" />
      <h1 className="mt-4 font-display text-2xl font-semibold">{error ? "Couldn't join the team" : "Joining the team…"}</h1>
      {error && (
        <>
          <p data-testid="join-team-error" className="mt-2 text-sm text-[#FF8FA3]">{error}</p>
          <button data-testid="join-team-dismiss" onClick={dismiss} className="mt-6 rounded-full border border-[#2A2A2A] px-5 py-2.5 text-sm hover:border-white transition-colors">Continue without joining</button>
        </>
      )}
    </div>
  );
};

export const ViewerBanner = () => (
  <div data-testid="viewer-banner" className="flex items-center gap-2 border-b border-[#2A2A2A] bg-[#121212] px-4 md:px-8 py-2 text-xs text-[#A3A3A3]">
    <Eye size={14} className="text-[#00C2FF]" /> You have view-only access. You can look at everything and ask the Calibrator, but you can't change anything.
  </div>
);

export const TeamPaused = () => (
  <div data-testid="team-paused" className="max-w-xl">
    <PauseCircle size={32} className="text-[#FFB800]" />
    <h1 className="font-display text-3xl font-semibold mt-3">Team access is paused</h1>
    <p className="mt-3 text-[#A3A3A3]">This workspace isn't on the Pro plan right now. Ask the owner to upgrade to bring team access back. You can still manage your own login on Account &amp; privacy.</p>
  </div>
);

export const LeaveTeamButton = ({ className = "" }) => {
  const api = useApi();
  const qc = useQueryClient();
  const leave = async () => {
    if (!window.confirm("Leave this team? You'll lose access to the workspace, and your private Calibrator chats there are erased. Your login stays.")) return;
    try { await api.post("/team/leave"); toast.success("You left the team"); qc.invalidateQueries(); }
    catch (e) { toast.error(errText(e, "Couldn't leave the team")); }
  };
  return <button data-testid="leave-team-btn" onClick={leave} className={`rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-white transition-colors ${className}`}>Leave team</button>;
};

export const MfaSetupRequired = () => (
  <div data-testid="mfa-setup-required" className="max-w-3xl space-y-4">
    <h1 className="font-display text-3xl font-semibold">Set up two-step sign-in to continue</h1>
    <p className="text-sm text-[#A3A3A3]">The owner of this workspace requires two-step sign-in for everyone on the team.</p>
    <SecuritySection required />
    <p className="text-xs text-[#6B6B6B]">Don't want to set it up? <LeaveTeamButton className="ml-2" /></p>
  </div>
);
